import { useCallback, useMemo } from "react";
import { useGetNationalityList, useGetResidenceStatusList, useGetLanguageTestList } from "@/api/services/global/master";
import { useEnum } from "@/api/services/global/enum";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { useTalentFilterOptions } from "@/api/services/main/talent";
import { useGetPartnerList } from "@/api/services/main/partner";
import { truncateWithEllipsis } from "@/utils/string";
import { TOption } from "@/types/common";
import { FILTER_LABEL_MAX_LENGTH } from "@/features/case/components/CaseMatches/case-match-filter-shared";

/** Static options for the 他提案 (other proposals) filter */
const HAS_OTHER_PROPOSAL_OPTIONS: TOption[] = [
  { label: "あり", value: "1" },
  { label: "なし", value: "0" },
];

/**
 * Hook to fetch all master data needed for case match filter dropdowns.
 * Returns both truncated options (for display in dropdowns) and raw options (for label resolution).
 */
export const useCaseMatchFilterMasterData = () => {
  const { nationalityOptions } = useGetNationalityList();
  const { residenceStatusOptions } = useGetResidenceStatusList();
  const { data: genderOptions } = useEnum("EGender");
  const { languageTestLevelOptions } = useGetLanguageTestList();
  const { userOptions: personInChargeOptions } = useGetUserListPersonInCharge();
  const { options: partnerOptions } = useGetPartnerList("");
  const { talentNumberOptions, fullNameOptions, nickNameOptions } = useTalentFilterOptions();

  const truncateOpts = useCallback(
    (options: TOption[], maxLength = FILTER_LABEL_MAX_LENGTH) => options.map((opt) => ({ ...opt, label: truncateWithEllipsis(opt.label, maxLength) })),
    [],
  );

  const truncatedOptions = useMemo(
    () => ({
      talentNumber: truncateOpts(talentNumberOptions),
      fullName: truncateOpts(fullNameOptions),
      nickName: truncateOpts(nickNameOptions),
      nationality: truncateOpts(nationalityOptions),
      gender: truncateOpts(genderOptions ?? []),
      residenceStatus: truncateOpts(residenceStatusOptions ?? []),
      japaneseLevelType: truncateOpts(languageTestLevelOptions),
      personInCharge: truncateOpts(personInChargeOptions),
    }),
    [truncateOpts, talentNumberOptions, fullNameOptions, nickNameOptions, nationalityOptions, genderOptions, residenceStatusOptions, languageTestLevelOptions, personInChargeOptions],
  );

  const rawOptions = useMemo(
    () => ({
      nationality: nationalityOptions,
      gender: genderOptions ?? [],
      residenceStatus: residenceStatusOptions ?? [],
      japaneseLevelType: languageTestLevelOptions,
      hasOtherProposal: HAS_OTHER_PROPOSAL_OPTIONS,
      partner: partnerOptions,
      personInCharge: personInChargeOptions,
    }),
    [nationalityOptions, genderOptions, residenceStatusOptions, languageTestLevelOptions, partnerOptions, personInChargeOptions],
  );

  return { truncatedOptions, rawOptions };
};
